/**
 * API Service for WeatherTeller Website
 * 
 * This service handles communication with the WeatherTeller API,
 * providing methods to fetch statistics, reports, activity data,
 * weather information, and handle user authentication.
 */
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, 
  signOut, sendPasswordResetEmail, updateProfile } from 'firebase/auth';

// Base API URL - would point to the deployed API in production
// Using HTTPS as confirmed by the user
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://weatherteller-api.web.app';

// Default request cache lifetime (5 minutes)
const DEFAULT_CACHE_LIFETIME = 5 * 60 * 1000;

// Request controller storage for cancellation support
const activeRequests = new Map();

// In-memory cache for responses
const responseCache = new Map();

/**
 * Generic API request handler with error handling, caching, and cancellation support
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Request options
 * @param {boolean} requiresAuth - Whether this endpoint requires authentication
 * @param {boolean} useCache - Whether to use cached responses
 * @param {number} cacheDuration - Cache duration in milliseconds
 * @returns {Promise<Object>} - API response data
 */
const apiRequest = async (
  endpoint, 
  options = {}, 
  requiresAuth = false, 
  useCache = false,
  cacheDuration = DEFAULT_CACHE_LIFETIME
) => {
  // Generate cache key if caching is enabled
  const cacheKey = useCache ? `${endpoint}|${JSON.stringify(options)}` : null;
  
  // Check cache for valid response
  if (useCache && cacheKey) {
    const cachedItem = responseCache.get(cacheKey);
    if (cachedItem && Date.now() < cachedItem.expiry) {
      return cachedItem.data;
    }
  }
  
  // Cancel any existing requests to this endpoint
  if (activeRequests.has(endpoint)) {
    activeRequests.get(endpoint).abort();
  }
  
  // Create new request controller for cancellation support
  const controller = new AbortController();
  activeRequests.set(endpoint, controller);
  
  try {
    // Prepare request URL and default headers
    const url = `${API_BASE_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    // Add API key if available and authentication is required
    if (requiresAuth) {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (user) {
        const token = await user.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        throw new Error('Authentication required for this endpoint');
      }
    }

    // Execute request with abort signal
    const response = await fetch(url, {
      ...options,
      headers,
      signal: controller.signal
    });

    // Handle HTTP errors
    if (!response.ok) {
      let errorMessage = `API error: ${response.status} ${response.statusText}`;
      
      try {
        const errorData = await response.json();
        if (errorData && errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // If we can't parse the error response, fall back to the default message
      }
      
      throw new Error(errorMessage);
    }

    // Parse response data
    const data = await response.json();
    
    // Cache response if caching is enabled
    if (useCache && cacheKey) {
      responseCache.set(cacheKey, {
        data,
        expiry: Date.now() + cacheDuration
      });
    }
    
    return data;
  } catch (error) {
    // Don't log AbortError as it's an expected cancellation
    if (error.name !== 'AbortError') {
      console.error(`API Request Error (${endpoint}):`, error);
    }
    throw error;
  } finally {
    // Clean up request controller
    if (activeRequests.get(endpoint) === controller) {
      activeRequests.delete(endpoint);
    }
  }
};

/**
 * Statistics API Services
 */
export const statisticsApi = {
  /**
   * Get community metrics
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Community metrics data
   */
  getMetrics: async (useCache = true) => {
    return apiRequest('/api/statistics/metrics', {}, false, useCache);
  },

  /**
   * Get weather trends
   * @param {number} days - Number of days to get trends for
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Weather trends data
   */
  getTrends: async (days = 7, useCache = true) => {
    return apiRequest(`/api/statistics/trends?days=${days}`, {}, false, useCache);
  },

  /**
   * Get top contributors
   * @param {number} limit - Maximum number of contributors to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Top contributors data
   */
  getTopContributors: async (limit = 10, useCache = true) => {
    return apiRequest(`/api/statistics/contributors/top?limit=${limit}`, {}, false, useCache);
  },

  /**
   * Get regional activity statistics
   * @param {number} limit - Maximum number of regions to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Regional activity data
   */
  getRegionalActivity: async (limit = 20, useCache = true) => {
    return apiRequest(`/api/statistics/regional-activity?limit=${limit}`, {}, false, useCache);
  }
};

/**
 * Reports API Services
 */
export const reportsApi = {
  /**
   * Get recent weather reports
   * @param {number} limit - Maximum number of reports to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Recent reports data
   */
  getRecent: async (limit = 30, useCache = true) => {
    return apiRequest(`/api/reports/recent?limit=${limit}`, {}, false, useCache);
  },

  /**
   * Get weather reports near a location
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {number} radius - Search radius in kilometers
   * @param {number} limit - Maximum number of reports to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Nearby reports data
   */
  getNearby: async (lat, lng, radius = 10, limit = 30, useCache = true) => {
    return apiRequest(
      `/api/reports/nearby?lat=${lat}&lng=${lng}&radius=${radius}&limit=${limit}`,
      {},
      false,
      useCache
    );
  },

  /**
   * Get a specific weather report by ID
   * @param {string} id - Report ID
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Report data
   */
  getById: async (id, useCache = true) => {
    return apiRequest(`/api/reports/${id}`, {}, false, useCache);
  },

  /**
   * Get reports from a specific time period
   * @param {number} start - Start timestamp
   * @param {number} end - End timestamp
   * @param {number} limit - Maximum number of reports to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Reports data
   */
  getByTimePeriod: async (start, end, limit = 50, useCache = true) => {
    return apiRequest(
      `/api/reports/time-period?start=${start}&end=${end}&limit=${limit}`,
      {},
      false,
      useCache
    );
  },

  /**
   * Submit a new weather report (requires authentication)
   * @param {Object} reportData - Report data
   * @returns {Promise<Object>} - Submission result
   */
  submitReport: async (reportData) => {
    return apiRequest('/api/reports', {
      method: 'POST',
      body: JSON.stringify(reportData)
    }, true, false);
  },

  /**
   * Update an existing report (requires authentication and ownership)
   * @param {string} id - Report ID
   * @param {Object} reportData - Updated report data
   * @returns {Promise<Object>} - Update result
   */
  updateReport: async (id, reportData) => {
    return apiRequest(`/api/reports/${id}`, {
      method: 'PUT',
      body: JSON.stringify(reportData)
    }, true, false);
  },

  /**
   * Delete a report (requires authentication and ownership)
   * @param {string} id - Report ID
   * @returns {Promise<Object>} - Deletion result
   */
  deleteReport: async (id) => {
    return apiRequest(`/api/reports/${id}`, {
      method: 'DELETE'
    }, true, false);
  }
};

/**
 * Activity API Services
 */
export const activityApi = {
  /**
   * Get recent activity
   * @param {number} limit - Maximum number of activities to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Recent activity data
   */
  getRecent: async (limit = 30, useCache = true) => {
    return apiRequest(`/api/activity/recent?limit=${limit}`, {}, false, useCache);
  },

  /**
   * Get recent contributor activity
   * @param {number} limit - Maximum number of activities to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Contributor activity data
   */
  getContributors: async (limit = 10, useCache = true) => {
    return apiRequest(`/api/activity/contributors?limit=${limit}`, {}, false, useCache);
  },

  /**
   * Get activity summary
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Activity summary data
   */
  getSummary: async (useCache = true) => {
    return apiRequest('/api/activity/summary', {}, false, useCache);
  },

  /**
   * Get daily activity breakdown
   * @param {number} days - Number of days to get activity for
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Daily activity data
   */
  getDaily: async (days = 7, useCache = true) => {
    return apiRequest(`/api/activity/daily?days=${days}`, {}, false, useCache);
  },

  /**
   * Get activity heat map data
   * @param {number} limit - Maximum number of points to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Heat map data
   */
  getHeatmap: async (limit = 100, useCache = true) => {
    return apiRequest(`/api/activity/heatmap?limit=${limit}`, {}, false, useCache);
  }
};

/**
 * Weather API Services
 */
export const weatherApi = {
  /**
   * Get current weather for a location
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Current weather data
   */
  getCurrent: async (lat, lng, useCache = true) => {
    return apiRequest(`/api/weather/current?lat=${lat}&lng=${lng}`, {}, false, useCache);
  },

  /**
   * Get weather forecast for a location
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Forecast data
   */
  getForecast: async (lat, lng, useCache = true) => {
    return apiRequest(`/api/weather/forecast?lat=${lat}&lng=${lng}`, {}, false, useCache);
  },

  /**
   * Get user-generated weather reports for a location
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {number} radius - Search radius in kilometers
   * @param {number} limit - Maximum number of reports to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Weather reports data
   */
  getReports: async (lat, lng, radius = 10, limit = 20, useCache = true) => {
    return apiRequest(
      `/api/weather/reports?lat=${lat}&lng=${lng}&radius=${radius}&limit=${limit}`,
      {},
      false,
      useCache
    );
  },

  /**
   * Get combined weather data (current, forecast, and reports)
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {number} radius - Search radius in kilometers for reports
   * @param {number} limit - Maximum number of reports to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Combined weather data
   */
  getCombined: async (lat, lng, radius = 10, limit = 10, useCache = true) => {
    return apiRequest(
      `/api/weather/combined?lat=${lat}&lng=${lng}&radius=${radius}&limit=${limit}`,
      {},
      false,
      useCache
    );
  }
};

/**
 * Authentication API Services
 */
export const authApi = {
  /**
   * Register a new user
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {string} displayName - User display name
   * @returns {Promise<Object>} - Registration result
   */
  register: async (email, password, displayName) => {
    try {
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Set display name after registration
      await updateProfile(userCredential.user, {
        displayName
      });
      
      // Register user on the backend
      await apiRequest('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          uid: userCredential.user.uid,
          email,
          displayName
        })
      }, true, false);
      
      return {
        success: true,
        user: userCredential.user
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Log in an existing user
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} - Login result
   */
  login: async (email, password) => {
    try {
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      return {
        success: true,
        user: userCredential.user
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Log out the current user
   * @returns {Promise<Object>} - Logout result
   */
  logout: async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      
      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Send password reset email
   * @param {string} email - User email
   * @returns {Promise<Object>} - Reset result
   */
  resetPassword: async (email) => {
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, email);
      
      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Get current user data
   * @returns {Object|null} - Current user data or null if not logged in
   */
  getCurrentUser: () => {
    const auth = getAuth();
    return auth.currentUser;
  },

  /**
   * Request admin approval for a user (requires authentication)
   * @param {string} userId - User ID to approve
   * @returns {Promise<Object>} - Approval request result
   */
  requestApproval: async (userId) => {
    return apiRequest('/api/auth/request-approval', {
      method: 'POST',
      body: JSON.stringify({ userId })
    }, true, false);
  },

  /**
   * Approve a user (requires admin authentication)
   * @param {string} userId - User ID to approve
   * @returns {Promise<Object>} - Approval result
   */
  approveUser: async (userId) => {
    return apiRequest('/api/auth/approve-user', {
      method: 'POST',
      body: JSON.stringify({ userId })
    }, true, false);
  },

  /**
   * Get list of users pending approval (requires admin authentication)
   * @returns {Promise<Object>} - List of pending users
   */
  getPendingApprovals: async () => {
    return apiRequest('/api/auth/pending-approvals', {}, true, false);
  }
};

/**
 * User Profile API Services
 */
export const profileApi = {
  /**
   * Get current user profile (requires authentication)
   * @returns {Promise<Object>} - User profile data
   */
  getProfile: async () => {
    return apiRequest('/api/profile', {}, true, false);
  },

  /**
   * Update user profile (requires authentication)
   * @param {Object} profileData - Updated profile data
   * @returns {Promise<Object>} - Update result
   */
  updateProfile: async (profileData) => {
    return apiRequest('/api/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData)
    }, true, false);
  },

  /**
   * Get public profile for any user
   * @param {string} userId - User ID
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Public profile data
   */
  getPublicProfile: async (userId, useCache = true) => {
    return apiRequest(`/api/profile/${userId}/public`, {}, false, useCache);
  },

  /**
   * Get user activity history (requires authentication for own activity)
   * @param {string} userId - User ID (use 'me' for current user)
   * @param {number} limit - Maximum number of activities to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - User activity data
   */
  getUserActivity: async (userId, limit = 30, useCache = true) => {
    // If requesting own activity, require authentication
    const requiresAuth = userId === 'me';
    const endpoint = requiresAuth ? 
      `/api/profile/me/activity?limit=${limit}` : 
      `/api/profile/${userId}/activity?limit=${limit}`;
    
    return apiRequest(endpoint, {}, requiresAuth, useCache);
  }
};

/**
 * Search API Services
 */
export const searchApi = {
  /**
   * Perform a global search across all content
   * @param {string} query - Search query
   * @param {number} limit - Maximum number of results to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Search results
   */
  global: async (query, limit = 20, useCache = false) => {
    return apiRequest(`/api/search?q=${encodeURIComponent(query)}&limit=${limit}`, {}, false, useCache);
  },

  /**
   * Search for locations
   * @param {string} query - Search query
   * @param {number} limit - Maximum number of results to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Location search results
   */
  locations: async (query, limit = 10, useCache = true) => {
    return apiRequest(`/api/search/locations?q=${encodeURIComponent(query)}&limit=${limit}`, {}, false, useCache);
  },

  /**
   * Search for users
   * @param {string} query - Search query
   * @param {number} limit - Maximum number of results to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - User search results
   */
  users: async (query, limit = 10, useCache = true) => {
    return apiRequest(`/api/search/users?q=${encodeURIComponent(query)}&limit=${limit}`, {}, false, useCache);
  },

  /**
   * Search for reports with filters
   * @param {Object} filters - Search filters
   * @param {number} limit - Maximum number of results to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Report search results
   */
  reports: async (filters = {}, limit = 20, useCache = false) => {
    // Convert filters object to query string
    const queryParams = new URLSearchParams();
    
    if (filters.query) queryParams.append('q', filters.query);
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);
    if (filters.lat) queryParams.append('lat', filters.lat);
    if (filters.lng) queryParams.append('lng', filters.lng);
    if (filters.radius) queryParams.append('radius', filters.radius);
    if (filters.condition) queryParams.append('condition', filters.condition);
    if (filters.userId) queryParams.append('userId', filters.userId);
    
    queryParams.append('limit', limit);
    
    return apiRequest(`/api/search/reports?${queryParams.toString()}`, {}, false, useCache);
  },

  /**
   * Get search suggestions as you type
   * @param {string} query - Partial search query
   * @param {string} type - Type of suggestions (locations, users, etc.)
   * @param {number} limit - Maximum number of suggestions to return
   * @returns {Promise<Object>} - Search suggestions
   */
  getSuggestions: async (query, type = 'all', limit = 5) => {
    return apiRequest(
      `/api/search/suggestions?q=${encodeURIComponent(query)}&type=${type}&limit=${limit}`,
      {},
      false,
      false
    );
  }
};

/**
 * Map and Location API Services
 */
export const mapApi = {
  /**
   * Get map grid data for a specific region
   * @param {number} lat - Latitude of center point
   * @param {number} lng - Longitude of center point
   * @param {number} zoom - Map zoom level
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Map grid data
   */
  getGridData: async (lat, lng, zoom, useCache = true) => {
    return apiRequest(`/api/map/grid?lat=${lat}&lng=${lng}&zoom=${zoom}`, {}, false, useCache);
  },

  /**
   * Get heatmap data for a specific region
   * @param {number} lat - Latitude of center point
   * @param {number} lng - Longitude of center point
   * @param {number} radius - Radius in kilometers
   * @param {string} dataType - Type of data for heatmap (reports, activity, etc.)
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Heatmap data
   */
  getHeatmapData: async (lat, lng, radius = 25, dataType = 'reports', useCache = true) => {
    return apiRequest(
      `/api/map/heatmap?lat=${lat}&lng=${lng}&radius=${radius}&dataType=${dataType}`,
      {},
      false,
      useCache
    );
  },

  /**
   * Get clustered map markers
   * @param {number} lat - Latitude of center point
   * @param {number} lng - Longitude of center point
   * @param {number} zoom - Map zoom level
   * @param {number} radius - Radius in kilometers
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Clustered markers data
   */
  getClusteredMarkers: async (lat, lng, zoom, radius = 50, useCache = true) => {
    return apiRequest(
      `/api/map/clusters?lat=${lat}&lng=${lng}&zoom=${zoom}&radius=${radius}`,
      {},
      false,
      useCache
    );
  },

  /**
   * Geocode an address to coordinates
   * @param {string} address - Address to geocode
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Geocoding result
   */
  geocode: async (address, useCache = true) => {
    return apiRequest(
      `/api/map/geocode?address=${encodeURIComponent(address)}`,
      {},
      false,
      useCache
    );
  },

  /**
   * Reverse geocode coordinates to an address
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Reverse geocoding result
   */
  reverseGeocode: async (lat, lng, useCache = true) => {
    return apiRequest(`/api/map/reverse-geocode?lat=${lat}&lng=${lng}`, {}, false, useCache);
  }
};

/**
 * Notifications API Services
 */
export const notificationsApi = {
  /**
   * Get user notifications (requires authentication)
   * @param {number} limit - Maximum number of notifications to return
   * @param {boolean} includeRead - Whether to include read notifications
   * @returns {Promise<Object>} - User notifications
   */
  getNotifications: async (limit = 20, includeRead = false) => {
    return apiRequest(
      `/api/notifications?limit=${limit}&includeRead=${includeRead}`,
      {},
      true,
      false
    );
  },

  /**
   * Mark notifications as read (requires authentication)
   * @param {Array|string} ids - Notification ID(s) to mark as read
   * @returns {Promise<Object>} - Result
   */
  markAsRead: async (ids) => {
    const notificationIds = Array.isArray(ids) ? ids : [ids];
    
    return apiRequest('/api/notifications/mark-read', {
      method: 'POST',
      body: JSON.stringify({ ids: notificationIds })
    }, true, false);
  },

  /**
   * Update notification preferences (requires authentication)
   * @param {Object} preferences - Notification preferences
   * @returns {Promise<Object>} - Result
   */
  updatePreferences: async (preferences) => {
    return apiRequest('/api/notifications/preferences', {
      method: 'PUT',
      body: JSON.stringify(preferences)
    }, true, false);
  },
  
  /**
   * Send a push notification subscription to the server (requires authentication)
   * @param {Object} subscription - Push subscription object from the browser
   * @returns {Promise<Object>} - Result
   */
  subscribePush: async (subscription) => {
    return apiRequest('/api/notifications/push-subscription', {
      method: 'POST',
      body: JSON.stringify(subscription)
    }, true, false);
  }
};

/**
 * Community API Services
 */
export const communityApi = {
  /**
   * Vote on a weather report (requires authentication)
   * @param {string} reportId - Report ID
   * @param {boolean} isUpvote - Whether this is an upvote
   * @returns {Promise<Object>} - Vote result
   */
  voteReport: async (reportId, isUpvote) => {
    return apiRequest('/api/community/vote', {
      method: 'POST',
      body: JSON.stringify({
        reportId,
        voteType: isUpvote ? 'up' : 'down'
      })
    }, true, false);
  },

  /**
   * Vote on a feature request (requires authentication)
   * @param {string} featureId - Feature ID
   * @param {boolean} isUpvote - Whether this is an upvote
   * @returns {Promise<Object>} - Vote result
   */
  voteFeature: async (featureId, isUpvote) => {
    return apiRequest('/api/community/feature-vote', {
      method: 'POST',
      body: JSON.stringify({
        featureId,
        voteType: isUpvote ? 'up' : 'down'
      })
    }, true, false);
  },

  /**
   * Follow a user (requires authentication)
   * @param {string} userId - User ID to follow
   * @returns {Promise<Object>} - Follow result
   */
  followUser: async (userId) => {
    return apiRequest('/api/community/follow', {
      method: 'POST',
      body: JSON.stringify({ userId })
    }, true, false);
  },

  /**
   * Unfollow a user (requires authentication)
   * @param {string} userId - User ID to unfollow
   * @returns {Promise<Object>} - Unfollow result
   */
  unfollowUser: async (userId) => {
    return apiRequest('/api/community/unfollow', {
      method: 'POST',
      body: JSON.stringify({ userId })
    }, true, false);
  },

  /**
   * Get followers for a user
   * @param {string} userId - User ID
   * @param {number} limit - Maximum number of followers to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Followers data
   */
  getFollowers: async (userId, limit = 20, useCache = true) => {
    return apiRequest(`/api/community/followers/${userId}?limit=${limit}`, {}, false, useCache);
  },

  /**
   * Get users followed by a user
   * @param {string} userId - User ID
   * @param {number} limit - Maximum number of followed users to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Followed users data
   */
  getFollowing: async (userId, limit = 20, useCache = true) => {
    return apiRequest(`/api/community/following/${userId}?limit=${limit}`, {}, false, useCache);
  },
  
  /**
   * Submit feedback (requires authentication)
   * @param {Object} feedback - Feedback data
   * @returns {Promise<Object>} - Submission result
   */
  submitFeedback: async (feedback) => {
    return apiRequest('/api/community/feedback', {
      method: 'POST',
      body: JSON.stringify(feedback)
    }, true, false);
  },
  
  /**
   * Get feature voting dashboard
   * @param {number} limit - Maximum number of features to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Feature voting data
   */
  getFeatureVoting: async (limit = 20, useCache = true) => {
    return apiRequest(`/api/community/feature-voting?limit=${limit}`, {}, false, useCache);
  }
};

/**
 * Settings API Services
 */
export const settingsApi = {
  /**
   * Get user settings (requires authentication)
   * @returns {Promise<Object>} - User settings
   */
  getSettings: async () => {
    return apiRequest('/api/settings', {}, true, false);
  },

  /**
   * Update user settings (requires authentication)
   * @param {Object} settings - Updated settings
   * @returns {Promise<Object>} - Update result
   */
  updateSettings: async (settings) => {
    return apiRequest('/api/settings', {
      method: 'PUT',
      body: JSON.stringify(settings)
    }, true, false);
  },

  /**
   * Get app configuration
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - App configuration
   */
  getAppConfig: async (useCache = true) => {
    return apiRequest('/api/settings/app-config', {}, false, useCache);
  }
};

/**
 * Leaderboard API Services
 */
export const leaderboardApi = {
  /**
   * Get leaderboard data
   * @param {string} type - Leaderboard type (contributors, accurate, etc.)
   * @param {string} timeframe - Time frame (all-time, weekly, monthly)
   * @param {number} limit - Maximum number of entries to return
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - Leaderboard data
   */
  getLeaderboard: async (type = 'contributors', timeframe = 'monthly', limit = 20, useCache = true) => {
    return apiRequest(
      `/api/leaderboard?type=${type}&timeframe=${timeframe}&limit=${limit}`,
      {},
      false,
      useCache
    );
  },

  /**
   * Get user's rank on leaderboard (requires authentication)
   * @param {string} type - Leaderboard type (contributors, accurate, etc.)
   * @param {string} timeframe - Time frame (all-time, weekly, monthly)
   * @returns {Promise<Object>} - User's rank data
   */
  getUserRank: async (type = 'contributors', timeframe = 'monthly') => {
    return apiRequest(`/api/leaderboard/my-rank?type=${type}&timeframe=${timeframe}`, {}, true, false);
  },
  
  /**
   * Get achievements for a user
   * @param {string} userId - User ID
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} - User achievements
   */
  getUserAchievements: async (userId, useCache = true) => {
    return apiRequest(`/api/leaderboard/achievements/${userId}`, {}, false, useCache);
  }
};

// Combine all APIs into a single export for convenience
export default {
  statistics: statisticsApi,
  reports: reportsApi,
  activity: activityApi,
  weather: weatherApi,
  auth: authApi,
  profile: profileApi,
  search: searchApi,
  map: mapApi,
  notifications: notificationsApi,
  community: communityApi,
  settings: settingsApi,
  leaderboard: leaderboardApi,
  
  // Utility methods for API consumers
  clearCache: () => responseCache.clear(),
  cancelAllRequests: () => {
    for (const controller of activeRequests.values()) {
      controller.abort();
    }
    activeRequests.clear();
  }
};